/**
 * Everything the contract screens compute rather than render.
 *
 * React-free on purpose: the localised-field pick below has a failure mode a
 * screenshot will not show (a blank name rather than a wrong one), so it has to
 * be readable without mounting anything.
 */
import { formatCents } from "../../../lib/sales";

/**
 * One localised template field in the reader's language.
 *
 * `??` alone is not enough here. The API's `localized` schema makes `et`
 * required and `en` optional with `.default("")` (see `contracts.routes.ts`),
 * so an English reader looking at a template nobody translated gets an empty
 * string rather than a missing key - and `??` passes an empty string straight
 * through. The name then renders blank instead of falling back, which reads as
 * a broken template rather than an untranslated one.
 */
export function locField(field: Record<string, string> | undefined, locale: string): string {
  if (!field) return "";
  const own = field[locale];
  if (own) return own;
  // `et` is the one the schema guarantees is present and non-empty.
  if (field.et) return field.et;
  return Object.values(field).find(Boolean) ?? "";
}

/** Money that may not exist. A contract with no value shows a dash, not
 *  "0 EUR": zero is a figure somebody agreed, absent is one nobody set. */
export function contractValue(cents: number | null | undefined): string {
  return cents == null ? "-" : formatCents(cents);
}

/** `<input type="date">` value to the ISO instant the API takes. */
export function toIso(v: string): string | null {
  return v ? new Date(v + "T00:00:00").toISOString() : null;
}

/** The other direction: an ISO instant back into a date field's value. */
export function dateInput(s: string | null): string {
  return s ? s.slice(0, 10) : "";
}

/** The contract body, which the API stores as either a string or `{ html }`. */
export function contentToText(content: unknown): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (typeof content === "object" && content && "html" in content) {
    return String((content as { html: unknown }).html ?? "");
  }
  return "";
}
